import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  Calibration,
  CalibrationResults,
  FinaliseCalibrationInputSet,
  VoltageReadings,
  CalibrationSlot,
  CalibrationUpdate,
  ManualCalibration,
  CalibrationNotification,
} from '../_models/calibration';
import { DeviceCalibrationSummary } from '../_models/device-calibration-summary';
import {
  OxygenCalibration,
  OxygenCalibrationUpdate,
} from '../_models/oxygen-calibration';
import { Module } from '../_models/module-enum';
import { PhCalibration, PhCalibrationUpdate } from '../_models/ph-calibration';
import {
  SpargingCalibration,
  SpargingCalibrationUpdate,
} from '../_models/sparging-calibration';
import { httpOptions } from '../_helpers/utils';
import { AuthenticationService } from './authentication.service';
import {
  CalibrationChangelogResponse,
  CalibrationResponse,
  OxygenCalibrationResponse,
  PhCalibrationResponse,
  SpargingCalibrationResponse,
} from '../_models/api-responses';
import { PaginationData } from '../_models/pagination-data.model';
import { CalibrationChangelogEntry } from '../_models/changelog';

interface UnsavedOdFormCache {
  calibrationType: string;
  calibrationId: string;
  data: FinaliseCalibrationInputSet;
}

@Injectable({
  providedIn: 'root',
})
export class CalibrationService {
  private readonly http = inject(HttpClient);
  private readonly authenticationService = inject(AuthenticationService);

  httpOptions = httpOptions;
  selectedlaboratoryId: number;

  // Pagination and sort/filter observables for device calibration index views
  private readonly tableSort$ = new BehaviorSubject<{
    sortBy: string;
    sortDirection: string;
  }>(null);
  private readonly tableFilter$ = new BehaviorSubject<string[]>([]);
  private readonly pageNumber$ = new BehaviorSubject<number>(null);
  private readonly pageSize$ = new BehaviorSubject<number>(null);
  private readonly createCalibrationSetup$ = new BehaviorSubject<string>(null);
  private readonly createCalibrationFormSubject$ = new BehaviorSubject<
    | Calibration
    | ManualCalibration
    | OxygenCalibration
    | PhCalibration
    | SpargingCalibration
  >(null);
  private readonly unsavedOdFormCache$ =
    new BehaviorSubject<UnsavedOdFormCache | null>(null);

  constructor() {
    this.selectedlaboratoryId =
      this.authenticationService.selectedLaboratory?.laboratoryId;
  }

  onUpdateSort = (sort: { sortBy: string; sortDirection: string }): void => {
    this.tableSort$.next(sort);
  };

  onUpdateFilter = (filter: string[]): void => {
    this.tableFilter$.next(filter);
  };

  onUpdatePageNumber = (pageNumber: number): void => {
    this.pageNumber$.next(pageNumber);
  };

  onUpdatePageSize = (pageSize: number): void => {
    this.pageSize$.next(pageSize);
  };

  onUpdateCreateCalibrationSetup = (calibrationType: string): void => {
    this.createCalibrationSetup$.next(calibrationType);
  };

  onUpdateCreateCalibrationFormCache = (
    calibration:
      | Calibration
      | ManualCalibration
      | OxygenCalibration
      | PhCalibration
      | SpargingCalibration,
  ): void => {
    this.createCalibrationFormSubject$.next(calibration);
  };

  public get filter(): string[] {
    return this.tableFilter$.value;
  }

  public get sort(): { sortBy: string; sortDirection: string } {
    return this.tableSort$.value;
  }

  public get paginationCache(): { pageNumber: number; pageSize: number } {
    if (this.pageNumber$.value !== null && this.pageSize$.value !== null) {
      return {
        pageNumber: this.pageNumber$.value,
        pageSize: this.pageSize$.value,
      };
    } else {
      return null;
    }
  }

  public get retrieveisCalibrationSetupCache(): string {
    const cached = this.createCalibrationSetup$.value;
    this.createCalibrationSetup$.next(null);
    return cached;
  }

  public get retrieveAndClearUnsavedCalibrationCache():
    | Calibration
    | ManualCalibration
    | OxygenCalibration
    | PhCalibration
    | SpargingCalibration {
    const cached = this.createCalibrationFormSubject$.value;
    this.clearUnsavedCache();
    return cached;
  }

  clearUnsavedCache(): void {
    this.createCalibrationFormSubject$.next(null);
  }

  cacheUnsavedOdFormData = (
    calibrationType: string,
    calibrationId: string,
    data: FinaliseCalibrationInputSet,
  ): void => {
    this.unsavedOdFormCache$.next({ calibrationType, calibrationId, data });
  };

  // One-shot: consumes the cache entirely. Returns the cached readings only
  // if they belong to the calibration currently being loaded, so a 401 cached
  // on one calibration can never be applied to a different one.
  retrieveAndClearUnsavedOdFormCache = (
    calibrationType: string,
    calibrationId: string,
  ): FinaliseCalibrationInputSet | null => {
    const cached = this.unsavedOdFormCache$.value;
    this.unsavedOdFormCache$.next(null);
    if (
      cached?.calibrationType === calibrationType &&
      cached?.calibrationId === calibrationId
    ) {
      return cached.data;
    }
    return null;
  };

  // HTTP GET /CALIBRATION/byDeviceArray
  getCalibrationsByDeviceArray(
    deviceList: DeviceCalibrationSummary[],
    isLoadedOnDevice = true,
    isFinalOnly = false,
  ): Observable<Calibration[]> {
    let url = `${environment.apiUrl}/calibrations/by-device-array?`;
    deviceList.forEach((device: DeviceCalibrationSummary) => {
      url = url + `deviceIds=${device.deviceId}&`;
    });
    url = url + `loadedOnDeviceOnly=${isLoadedOnDevice ? 'true' : 'false'}`;
    url = url + `&isFinalOnly=${isFinalOnly ? 'true' : 'false'}`;
    return this.http.get<Calibration[]>(url, this.httpOptions).pipe(
      map((response: HttpResponse<Calibration[]>) => {
        return response?.body;
      }),
    );
  }

  getOxygenCalibrationsByDeviceArray(
    deviceList: DeviceCalibrationSummary[],
    isLoadedOnDevice = true,
    isFinalOnly = false,
  ): Observable<OxygenCalibration[]> {
    let url = `${environment.apiUrl}/oxygen-calibrations/by-device-array?`;
    deviceList.forEach((device: DeviceCalibrationSummary) => {
      url = url + `deviceIds=${device.deviceId}&`;
    });
    url = url + `loadedOnDeviceOnly=${isLoadedOnDevice ? 'true' : 'false'}`;
    url = url + `&isFinalOnly=${isFinalOnly ? 'true' : 'false'}`;
    return this.http.get<OxygenCalibration[]>(url, this.httpOptions).pipe(
      map((response: HttpResponse<OxygenCalibration[]>) => {
        return response?.body;
      }),
    );
  }

  getPhCalibrationsByDeviceArray(
    deviceList: DeviceCalibrationSummary[],
    isLoadedOnDevice = true,
    isFinalOnly = false,
  ): Observable<PhCalibration[]> {
    let url = `${environment.apiUrl}/ph-calibrations/by-device-array?`;
    deviceList.forEach((device: DeviceCalibrationSummary) => {
      url = url + `deviceIds=${device.deviceId}&`;
    });
    url = url + `loadedOnDeviceOnly=${isLoadedOnDevice ? 'true' : 'false'}`;
    url = url + `&isFinalOnly=${isFinalOnly ? 'true' : 'false'}`;
    return this.http.get<PhCalibration[]>(url, this.httpOptions).pipe(
      map((response: HttpResponse<PhCalibration[]>) => {
        return response?.body;
      }),
    );
  }

  getSpargingCalibrationsByDeviceArray(
    deviceList: DeviceCalibrationSummary[],
    isLoadedOnDevice = true,
    isFinalOnly = false,
  ): Observable<SpargingCalibration[]> {
    let url = `${environment.apiUrl}/sparging-calibrations/by-device-array?`;
    deviceList.forEach((device: DeviceCalibrationSummary) => {
      url = url + `deviceIds=${device.deviceId}&`;
    });
    url = url + `loadedOnDeviceOnly=${isLoadedOnDevice ? 'true' : 'false'}`;
    url = url + `&isFinalOnly=${isFinalOnly ? 'true' : 'false'}`;
    return this.http.get<SpargingCalibration[]>(url, this.httpOptions).pipe(
      map((response: HttpResponse<SpargingCalibration[]>) => {
        return response?.body;
      }),
    );
  }

  // HTTP GET /CHANGE-LOGS
  getCalibrationChangelog(
    calibrationTypeId: number,
    calibrationId: string,
    sortBy = 'CreatedAt',
    sortOrder = 'asc',
    pageSize = 10,
    pageNumber = 1,
  ): Observable<CalibrationChangelogResponse> {
    return this.http
      .get<
        CalibrationChangelogEntry[]
      >(`${environment.apiUrl}/change-logs/calibrations/${calibrationTypeId}/${calibrationId}?OrderBy=${sortBy}&OrderByDirection=${sortOrder}&PageSize=${pageSize}&PageNumber=${pageNumber}`, this.httpOptions)
      .pipe(
        map((response) => {
          // Set pagination data
          const paginationData = JSON.parse(
            response.headers.get('X-Pagination'),
          ) as PaginationData;

          return { changelogList: response.body, paginationData };
        }),
      );
  }

  // HTTP POST /calibration
  createODCalibration(calibration: Calibration): Observable<number> {
    calibration.laboratoryId = this.selectedlaboratoryId;
    return this.http
      .post<number>(`${environment.apiUrl}/calibrations`, calibration)
      .pipe(
        map((response: number) => {
          return response;
        }),
      );
  }

  // HTTP POST /calibrations/manual
  createManualODCalibration(
    manualCalibration: ManualCalibration,
  ): Observable<number> {
    manualCalibration.laboratoryId = this.selectedlaboratoryId;
    return this.http
      .post<number>(
        `${environment.apiUrl}/calibrations/manual`,
        manualCalibration,
      )
      .pipe(
        map((response: number) => {
          return response;
        }),
      );
  }

  // HTTP POST /oxygen-calibration
  createOxygenCalibration(calibration: OxygenCalibration): Observable<number> {
    calibration.laboratoryId = this.selectedlaboratoryId;
    return this.http
      .post<number>(`${environment.apiUrl}/oxygen-calibrations`, calibration)
      .pipe(
        map((response: number) => {
          return response;
        }),
      );
  }

  // HTTP POST /oxygen-calibration/manual
  createManualOxygenCalibration(
    calibration: OxygenCalibration,
  ): Observable<number> {
    calibration.laboratoryId = this.selectedlaboratoryId;
    return this.http
      .post<number>(
        `${environment.apiUrl}/oxygen-calibrations/manual`,
        calibration,
      )
      .pipe(
        map((response: number) => {
          return response;
        }),
      );
  }

  // HTTP POST /ph-calibration
  createPHCalibration(calibration: PhCalibration): Observable<number> {
    calibration.laboratoryId = this.selectedlaboratoryId;
    return this.http
      .post<number>(`${environment.apiUrl}/ph-calibrations`, calibration)
      .pipe(
        map((response: number) => {
          return response;
        }),
      );
  }

  // HTTP POST /ph-calibration/manual
  createManualPHCalibration(calibration: PhCalibration): Observable<number> {
    calibration.laboratoryId = this.selectedlaboratoryId;
    return this.http
      .post<number>(`${environment.apiUrl}/ph-calibrations/manual`, calibration)
      .pipe(
        map((response: number) => {
          return response;
        }),
      );
  }

  // HTTP POST /sparging-calibration
  createSpargingCalibration(
    calibration: SpargingCalibration,
  ): Observable<number> {
    calibration.laboratoryId = this.selectedlaboratoryId;
    return this.http
      .post<number>(`${environment.apiUrl}/sparging-calibrations`, calibration)
      .pipe(
        map((response: number) => {
          return response;
        }),
      );
  }

  // HTTP POST /sparging-calibration/manual
  createManualSpargingCalibration(
    calibration: SpargingCalibration,
  ): Observable<number> {
    calibration.laboratoryId = this.selectedlaboratoryId;
    return this.http
      .post<number>(
        `${environment.apiUrl}/sparging-calibrations/manual`,
        calibration,
      )
      .pipe(
        map((response: number) => {
          return response;
        }),
      );
  }

  // HTTP PATCH /CALIBRATIONS/{id}
  updateODCalibration(
    updatedODCalibration: CalibrationUpdate,
  ): Observable<Calibration> {
    // Ensure laboratory ID is set?
    return this.http
      .patch<Calibration>(
        `${environment.apiUrl}/calibrations/${updatedODCalibration.id}`,
        updatedODCalibration,
      )
      .pipe(
        map((response: Calibration) => {
          return response;
        }),
      );
  }

  // HTTP PATCH /OXYGEN-CALIBRATIONS/{id}
  updateOxygenCalibration(
    updatedOxygenCalibration: OxygenCalibrationUpdate,
  ): Observable<OxygenCalibration> {
    // Ensure laboratory ID is set?
    return this.http
      .patch<OxygenCalibration>(
        `${environment.apiUrl}/oxygen-calibrations/${updatedOxygenCalibration.id}`,
        updatedOxygenCalibration,
      )
      .pipe(
        map((response: OxygenCalibration) => {
          return response;
        }),
      );
  }

  // HTTP PATCH /PH-CALIBRATIONS/{id}
  updatePHCalibration(
    updatedPHCalibration: PhCalibrationUpdate,
  ): Observable<PhCalibration> {
    // Ensure laboratory ID is set?
    return this.http
      .patch<PhCalibration>(
        `${environment.apiUrl}/ph-calibrations/${updatedPHCalibration.id}`,
        updatedPHCalibration,
      )
      .pipe(
        map((response: PhCalibration) => {
          return response;
        }),
      );
  }

  // HTTP PATCH /SPARGING-CALIBRATIONS/{id}
  updateSpargingCalibration(
    updatedSpargingCalibration: SpargingCalibrationUpdate,
  ): Observable<SpargingCalibration> {
    // Ensure laboratory ID is set?
    return this.http
      .patch<SpargingCalibration>(
        `${environment.apiUrl}/sparging-calibrations/${updatedSpargingCalibration.id}`,
        updatedSpargingCalibration,
      )
      .pipe(
        map((response: SpargingCalibration) => {
          return response;
        }),
      );
  }

  // HTTP GET /memorySlot/deviceId/moduleId/memorySlotDetails
  getSlotSaveLocations(
    deviceId: number,
    moduleId: Module,
  ): Observable<CalibrationSlot[]> {
    return this.http
      .get<
        CalibrationSlot[]
      >(`${environment.apiUrl}/memory-slots/${deviceId}/${moduleId}`)
      .pipe(
        map((response) => {
          return response;
        }),
      );
  }

  getCalibrationsByDeviceId(
    deviceId: number,
    sortBy = 'Name',
    sortOrder = 'asc',
    pageSize?: number,
    pageNumber?: number,
  ): Observable<CalibrationResponse> {
    let url = `${environment.apiUrl}/calibrations/${deviceId}/calibrations?OrderBy=${sortBy}&OrderByDirection=${sortOrder}`;

    if (pageNumber && pageSize) {
      url = url + `&PageSize=${pageSize}&PageNumber=${pageNumber}`;
    }

    return this.http.get<Calibration[]>(url, this.httpOptions).pipe(
      map((response) => {
        // Set pagination data
        const paginationData = JSON.parse(
          response.headers.get('X-Pagination'),
        ) as PaginationData;
        // Try explicitly building the response object due to issue with find index
        const calibrationList: Calibration[] = [];
        response.body.forEach((c: Calibration) => {
          calibrationList.push(c);
        });
        return {
          paginationData,
          calibrationList,
        };
      }),
    );
  }

  getOxygenCalibrationsByDeviceId(
    deviceId: string,
    sortBy = 'Name',
    sortOrder = 'asc',
    pageSize = 5,
    pageNumber = 1,
  ): Observable<OxygenCalibrationResponse> {
    let url = `${environment.apiUrl}/oxygen-calibrations/${deviceId}/oxygen-calibrations?OrderBy=${sortBy}&OrderByDirection=${sortOrder}`;
    if (pageNumber && pageSize) {
      url = url + `&PageSize=${pageSize}&PageNumber=${pageNumber}`;
    }
    return this.http.get<OxygenCalibration[]>(url, this.httpOptions).pipe(
      map((response: HttpResponse<OxygenCalibration[]>) => {
        // Set pagination data
        const paginationData = JSON.parse(
          response.headers.get('X-Pagination'),
        ) as PaginationData;
        // Try explicitly building the response object due to issue with find index
        const calibrationList: OxygenCalibration[] = [];
        response.body.forEach((c: OxygenCalibration) => {
          calibrationList.push(c);
        });
        return { paginationData, calibrationList };
      }),
    );
  }

  getPHCalibrationsByDeviceId(
    deviceId: string,
    sortBy = 'Name',
    sortOrder = 'asc',
    pageSize = 5,
    pageNumber = 1,
  ): Observable<PhCalibrationResponse> {
    let url = `${environment.apiUrl}/ph-calibrations/${deviceId}/ph-calibrations?OrderBy=${sortBy}&OrderByDirection=${sortOrder}`;
    if (pageNumber && pageSize) {
      url = url + `&PageSize=${pageSize}&PageNumber=${pageNumber}`;
    }
    return this.http.get<PhCalibration[]>(url, this.httpOptions).pipe(
      map((response: HttpResponse<PhCalibration[]>) => {
        // Set pagination data
        const paginationData = JSON.parse(
          response.headers.get('X-Pagination'),
        ) as PaginationData;
        const calibrationList: PhCalibration[] = [];
        response.body.forEach((c: PhCalibration) => {
          calibrationList.push(c);
        });
        return { calibrationList, paginationData };
      }),
    );
  }

  getSpargingCalibrationsByDeviceId(
    deviceId: string,
    sortBy = 'Name',
    sortOrder = 'asc',
    pageSize = 5,
    pageNumber = 1,
  ): Observable<SpargingCalibrationResponse> {
    let url = `${environment.apiUrl}/sparging-calibrations/${deviceId}/calibrations?OrderBy=${sortBy}&OrderByDirection=${sortOrder}`;
    if (pageNumber && pageSize) {
      url = url + `&PageSize=${pageSize}&PageNumber=${pageNumber}`;
    }
    return this.http.get<SpargingCalibration[]>(url, this.httpOptions).pipe(
      map((response: HttpResponse<SpargingCalibration[]>) => {
        // Set pagination data
        const paginationData = JSON.parse(
          response.headers.get('X-Pagination'),
        ) as PaginationData;
        const calibrationList: SpargingCalibration[] = [];
        response.body.forEach((c: SpargingCalibration) => {
          calibrationList.push(c);
        });
        return { calibrationList, paginationData };
      }),
    );
  }

  getODCalibrationById(id: string): Observable<CalibrationResults> {
    return this.http
      .get<CalibrationResults>(`${environment.apiUrl}/calibrations/${id}`)
      .pipe(
        map((response: CalibrationResults) => {
          return response;
        }),
      );
  }

  getOxygenCalibrationById(id: string): Observable<OxygenCalibration> {
    return this.http
      .get<OxygenCalibration>(`${environment.apiUrl}/oxygen-calibrations/${id}`)
      .pipe(
        map((response: OxygenCalibration) => {
          return response;
        }),
      );
  }

  getPhCalibrationById(id: string): Observable<PhCalibration> {
    return this.http
      .get<PhCalibration>(`${environment.apiUrl}/ph-calibrations/${id}`)
      .pipe(
        map((response: PhCalibration) => {
          return response;
        }),
      );
  }

  getSpargingCalibrationById(id: string): Observable<SpargingCalibration> {
    return this.http
      .get<SpargingCalibration>(
        `${environment.apiUrl}/sparging-calibrations/${id}`,
      )
      .pipe(
        map((response: SpargingCalibration) => {
          return response;
        }),
      );
  }

  // DELETE /calibration/{id} - only works for calibrations that are not final
  deleteODCalibrationById(id: string): Observable<boolean> {
    return this.http
      .delete<boolean>(`${environment.apiUrl}/calibrations/${id}`)
      .pipe(
        map((response: boolean) => {
          return response;
        }),
      );
  }

  // DELETE /oxygenCalibration/{id} - only works for calibrations that are not final
  deleteOxygenCalibrationById(id: string): Observable<boolean> {
    return this.http
      .delete<boolean>(`${environment.apiUrl}/oxygen-calibrations/${id}`)
      .pipe(
        map((response: boolean) => {
          return response;
        }),
      );
  }

  // DELETE /PHCalibration/{id} - only works for calibrations that are not final
  deletePhCalibrationById(id: string): Observable<boolean> {
    return this.http
      .delete<boolean>(`${environment.apiUrl}/ph-calibrations/${id}`)
      .pipe(
        map((response: boolean) => {
          return response;
        }),
      );
  }

  // DELETE /SpargingCalibration/{id} - only works for calibrations that are not final
  deleteSpargingCalibrationById(id: string): Observable<boolean> {
    return this.http
      .delete<boolean>(`${environment.apiUrl}/sparging-calibrations/${id}`)
      .pipe(
        map((response: boolean) => {
          return response;
        }),
      );
  }

  finaliseODCalibrationById(
    id: string,
    inputValues: FinaliseCalibrationInputSet,
  ): Observable<boolean> {
    return this.http
      .patch<boolean>(
        `${environment.apiUrl}/calibrations/${id}/finalise`,
        inputValues,
      )
      .pipe(
        map((response: boolean) => {
          return response;
        }),
      );
  }

  // HTTP PUT /calibrations/{id}/od-readings
  saveODCalibrationReadings(
    id: string,
    inputValues: FinaliseCalibrationInputSet,
  ): Observable<FinaliseCalibrationInputSet> {
    return this.http
      .put<FinaliseCalibrationInputSet>(
        `${environment.apiUrl}/calibrations/${id}/od-readings`,
        inputValues,
      )
      .pipe(
        map((response: FinaliseCalibrationInputSet) => {
          return response;
        }),
      );
  }

  // HTTP GET /calibrations/{id}/od-readings
  getODCalibrationReadings(
    id: string,
  ): Observable<FinaliseCalibrationInputSet | null> {
    return this.http
      .get<FinaliseCalibrationInputSet | null>(
        `${environment.apiUrl}/calibrations/${id}/od-readings`,
      )
      .pipe(
        map((response: FinaliseCalibrationInputSet | null) => {
          return response;
        }),
      );
  }

  getVoltageReadings(id: number): Observable<VoltageReadings> {
    return this.http
      .get<VoltageReadings>(
        `${environment.apiUrl}/calibrations/${id}/voltage-readings`,
      )
      .pipe(
        map((response: VoltageReadings) => {
          return response;
        }),
      );
  }

  resendCalibrationToDevice(
    calibrationId: number,
    saveLocation: number,
  ): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${environment.apiUrl}/calibrations/${calibrationId}/${saveLocation}/resend`,
        {},
      )
      .pipe(
        map((response: boolean) => {
          return response;
        }),
      );
  }

  resendOxygenCalibrationToDevice(
    calibrationId: number,
    saveLocation: number,
  ): Observable<OxygenCalibration> {
    return this.http
      .post<OxygenCalibration>(
        `${environment.apiUrl}/oxygen-calibrations/${calibrationId}/${saveLocation}/resend`,
        {},
      )
      .pipe(
        map((response: OxygenCalibration) => {
          return response;
        }),
      );
  }

  resendPHCalibrationToDevice(
    calibrationId: number,
    saveLocation: number,
  ): Observable<PhCalibration> {
    return this.http
      .post<PhCalibration>(
        `${environment.apiUrl}/ph-calibrations/${calibrationId}/${saveLocation}/resend`,
        {},
      )
      .pipe(
        map((response: PhCalibration) => {
          return response;
        }),
      );
  }

  resendSpargingCalibrationToDevice(
    calibrationId: number,
    saveLocation: number,
  ): Observable<SpargingCalibration> {
    return this.http
      .post<SpargingCalibration>(
        `${environment.apiUrl}/sparging-calibrations/${calibrationId}/${saveLocation}/resend`,
        {},
      )
      .pipe(
        map((response: SpargingCalibration) => {
          return response;
        }),
      );
  }

  setOxygenCalibrationNotifications(
    calibrationId: number,
    enable: boolean,
  ): Observable<CalibrationNotification> {
    return this.http
      .patch<CalibrationNotification>(
        `${environment.apiUrl}/oxygen-calibrations/${calibrationId}/notifications/${enable}`,
        {},
      )
      .pipe(
        map((response: CalibrationNotification) => {
          return response;
        }),
      );
  }
}
